import { useEffect, useState } from "react";
import {
  FileSpreadsheet,
  FileText,
  LogOut,
  MoreHorizontal,
  Moon,
  Settings,
  Sun,
  User,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { CalculationOutput } from "../types";
import { useAuth } from "../contexts/AuthContext";
import { PeriodMode, fmtEUR, suffix } from "../utils/period";
import PeriodToggle from "./PeriodToggle";
import LanguageSwitcher from "./LanguageSwitcher";

export default function Header({
  active,
  onChangeActive,
  periodMode,
  onChangePeriodMode,
  onDuplicate,
  output,
  onLoadExample,
  onReset,
  onSaveSimulation,
  onOpenSimulations,
  simulationsCount,
  onDownloadPDF,
  onDownloadCSV,
  onOpenPremium,
  showPremiumBanner = false,
  onDismissPremiumBanner,
  showStickySummary = true,
}: {
  active: "A" | "B";
  onChangeActive: (s: "A" | "B") => void;
  periodMode: PeriodMode;
  onChangePeriodMode: (m: PeriodMode) => void;
  onDuplicate: () => void;
  output: CalculationOutput | null;
  onLoadExample: () => void;
  onReset: () => void;
  onSaveSimulation: () => void;
  simulationsCount: number;
  onDownloadPDF?: () => void;
  onDownloadCSV?: () => void;
  onOpenPremium?: () => void;
  showPremiumBanner?: boolean;
  onDismissPremiumBanner?: () => void;
  showStickySummary?: boolean;
  onOpenSimulations?: () => void;
}) {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [dark, setDark] = useState(false);
  const isPremium = !!user;

  useEffect(() => {
    const saved = localStorage.getItem("ac-theme");
    const nextDark = saved === "dark";
    setDark(nextDark);
    document.documentElement.setAttribute("data-theme", nextDark ? "dark" : "light");
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", dark ? "dark" : "light");
    localStorage.setItem("ac-theme", dark ? "dark" : "light");
  }, [dark]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 400);
    const handleClickOutside = (e: MouseEvent) => {
      if (menuOpen && !(e.target as HTMLElement).closest(".more-menu-container")) {
        setMenuOpen(false);
      }
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    document.addEventListener("click", handleClickOutside);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("click", handleClickOutside);
    };
  }, [menuOpen]);

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  const bruto = (output as any)?.brutoFacturado ?? 0;
  const rendimento = (output as any)?.rendimientoNeto ?? 0;
  const neto = (output as any)?.netoFinalTrasRenta ?? output?.netoFiscalAnual ?? 0;

  const stickyItems = [
    { label: "Bruto", value: fmtEUR(bruto, periodMode), color: "var(--accent)" },
    { label: "Rend. neto", value: fmtEUR(rendimento, periodMode), color: "var(--success)" },
    { label: "Neto final", value: fmtEUR(neto, periodMode), color: "var(--amber)" },
  ];

  const closeMenu = () => setMenuOpen(false);

  return (
    <header className="hdr">
      <div id="google_translate_element" style={{ display: "none" }} />
      <div className="hdr-inner">
        <div className="header-left">
          <a className="brand-link" href="/" aria-label="Autónomo Claro">
            <div className="logo-mark">
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
                <path
                  d="M3 12L8 4L13 12"
                  stroke="white"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M5.5 9h5"
                  stroke="white"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  opacity=".5"
                />
              </svg>
            </div>
            <span className="logo-wordmark">
              autónomo<span>claro</span>
            </span>
          </a>

          <div className="lang-switch desktop-only">
            <LanguageSwitcher />
          </div>

          {isPremium && (
            <div className="scenario-toggle desktop-only">
              {(["A", "B"] as const).map((scenario) => (
                <button
                  key={scenario}
                  type="button"
                  className={`scenario-btn${active === scenario ? " active" : ""}`}
                  onClick={() => onChangeActive(scenario)}
                >
                  Escenario {scenario}
                </button>
              ))}
            </div>
          )}

          {scrolled && output && showStickySummary && (
            <div className="sticky-summary desktop-only">
              {stickyItems.map((kpi) => (
                <div className="sticky-kpi" key={kpi.label}>
                  <span className="sticky-kpi-label">{kpi.label}</span>
                  <span className="sticky-kpi-value" style={{ color: kpi.color }}>
                    {kpi.value}
                    {suffix(periodMode)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="header-center desktop-only">
          <PeriodToggle value={periodMode} onChange={onChangePeriodMode} />
        </div>

        <div className="header-actions">
          {!isPremium ? (
            <div className="auth-cta-group">
              <button
                type="button"
                className="auth-cta auth-cta-ghost"
                onClick={() => navigate("/login")}
              >
                Iniciar sesión
              </button>
              <button
                type="button"
                className="auth-cta auth-cta-primary"
                onClick={onOpenPremium}
              >
                <User size={15} />
                Alta Premium
              </button>
            </div>
          ) : (
            <>
              <button
                type="button"
                className="header-icon-btn profile-button desktop-only"
                onClick={() => navigate("/profile")}
                title="Mi perfil"
              >
                <Settings size={16} />
              </button>
              <div className="user-pill desktop-only">
                <User size={14} />
                <span>{user?.email}</span>
              </div>
              <button
                type="button"
                className="header-icon-btn signout-button desktop-only"
                onClick={handleSignOut}
                title="Cerrar sesión"
              >
                <LogOut size={16} />
              </button>
            </>
          )}

          <button
            type="button"
            className="header-icon-btn"
            onClick={() => setDark((value) => !value)}
            title={dark ? "Modo claro" : "Modo oscuro"}
          >
            {dark ? <Sun size={16} /> : <Moon size={16} />}
          </button>

          <div className="more-menu-container">
            <button
              type="button"
              className={`header-icon-btn${menuOpen ? " is-open" : ""}`}
              onClick={() => setMenuOpen((value) => !value)}
              title="Más opciones"
            >
              <MoreHorizontal size={18} />
            </button>

            {menuOpen && (
              <div className="more-menu">
                {!isPremium && (
                  <>
                    <button
                      type="button"
                      className="more-menu-item menu-auth"
                      onClick={() => {
                        navigate("/login");
                        closeMenu();
                      }}
                    >
                      <span>Entrar</span>
                      <span className="menu-counter">Acceso</span>
                    </button>
                    <button
                      type="button"
                      className="more-menu-item menu-auth"
                      onClick={() => {
                        onOpenPremium?.();
                        closeMenu();
                      }}
                    >
                      <span>Alta Premium</span>
                      <span className="menu-counter">Gratis</span>
                    </button>
                    <div className="more-menu-sep menu-auth" />
                  </>
                )}

                {isPremium && (
                  <button
                    type="button"
                    className="more-menu-item"
                    onClick={() => {
                      navigate("/profile");
                      closeMenu();
                    }}
                  >
                    <span>Mi perfil</span>
                    <Settings size={16} />
                  </button>
                )}

                <button
                  type="button"
                  className="more-menu-item"
                  onClick={() => {
                    onLoadExample();
                    closeMenu();
                  }}
                >
                  <span>Cargar ejemplo</span>
                  <span>📋</span>
                </button>

                <button
                  type="button"
                  className="more-menu-item"
                  onClick={() => {
                    onDuplicate();
                    closeMenu();
                  }}
                >
                  <span>Duplicar escenario</span>
                  <span>🔁</span>
                </button>

                <button
                  type="button"
                  className="more-menu-item danger"
                  onClick={() => {
                    onReset();
                    closeMenu();
                    onDismissPremiumBanner?.();
                  }}
                >
                  <span>Limpiar datos</span>
                  <span>🧹</span>
                </button>

                {isPremium && (
                  <div style={{ padding: "8px 10px" }}>
                    <PeriodToggle value={periodMode} onChange={onChangePeriodMode} />
                  </div>
                )}

                {isPremium && onOpenSimulations && (
                  <button
                    type="button"
                    className="more-menu-item"
                    onClick={() => {
                      onOpenSimulations();
                      closeMenu();
                    }}
                  >
                    <span>Mis simulaciones</span>
                    <span className="menu-counter">{simulationsCount}</span>
                  </button>
                )}

                {isPremium && (
                  <button
                    type="button"
                    className="more-menu-item"
                    disabled={!output}
                    onClick={() => {
                      onSaveSimulation();
                      closeMenu();
                    }}
                  >
                    <span>Guardar simulación</span>
                    <span>💾</span>
                  </button>
                )}

                {onDownloadPDF && (
                  <button
                    type="button"
                    className="more-menu-item"
                    disabled={!output}
                    onClick={() => {
                      onDownloadPDF();
                      closeMenu();
                    }}
                  >
                    <span>Descargar PDF</span>
                    <FileText size={16} />
                  </button>
                )}

                {onDownloadCSV && (
                  <button
                    type="button"
                    className="more-menu-item"
                    disabled={!output}
                    onClick={() => {
                      onDownloadCSV();
                      closeMenu();
                    }}
                  >
                    <span>Descargar CSV</span>
                    <FileSpreadsheet size={16} />
                  </button>
                )}

                {isPremium && (
                  <>
                    <div className="more-menu-sep" />
                    <button
                      type="button"
                      className="more-menu-item danger"
                      onClick={async () => {
                        await handleSignOut();
                        closeMenu();
                      }}
                    >
                      <span>Cerrar sesión</span>
                      <LogOut size={16} />
                    </button>
                  </>
                )}

                {!isPremium && showPremiumBanner && (
                  <div className="hero-free-note" style={{ padding: "8px 10px 2px" }}>
                    Alta Premium gratis para guardar, comparar y exportar.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
